import { useEffect, useState, type ReactNode, type FormEvent } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { listApprovedVendors, type VendorSummary } from '../../api/vendorsApi.js';
import { useMasterOptions } from './useMasterOptions.js';
import { GeographyCascade, EMPTY_GEO, type GeoSelection } from './GeographyCascade.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { unitLabel } from '../../../../shared/contracts/unit-label.js';
import { DateInput, validateDateSubmission } from '../../../../shared/components/DateInput.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { DataTable } from '../../../../shared/components/DataTable.js';

/** One approved t200 rate in effect during the period asked about. */
interface RateInEffect {
  readonly rateId: string;
  readonly rateCode: string;
  readonly rateValue: string | number;
  readonly unitOfMeasure: string;
  readonly effectiveFromDate: string;
  readonly effectiveToDate: string;
}

/** L17.04 — Snapshot lookup (`/admin/rate-master/snapshot-lookup`). Every
 *  approved rate in effect for a vendor, branding item and geography during a
 *  period — the published work-order rates billing charges at. */
export function M17SnapshotLookupPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { money } = useBusinessNumber();
  const { dayRange } = useBusinessDay();
  const { brandingItems, loadError } = useMasterOptions(token);
  const [vendors, setVendors] = useState<readonly VendorSummary[]>([]);
  const [vendorError, setVendorError] = useState<string | null>(null);
  const [vendorId, setVendorId] = useState('');
  const [brandingItemId, setBrandingItemId] = useState('');
  const [geo, setGeo] = useState<GeoSelection>(EMPTY_GEO);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [rates, setRates] = useState<readonly RateInEffect[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    listApprovedVendors(token)
      .then((v) => { if (alive) setVendors(v); })
      .catch((e) => { if (alive) setVendorError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setRates(null);
    if (!vendorId || !brandingItemId || !fromDate || !toDate) {
      setError(t('m17.snapshotLookup.errRequired'));
      return;
    }
    try {
      const r = await apiRequest<{ rates: readonly RateInEffect[] }>('/v1/m17/rates/calculate', {
        method: 'POST',
        token,
        body: { vendorId, brandingItemId, geoNodeIds: geo.pathNodeIds, asOfDate: fromDate, toDate },
      });
      setRates(r.rates);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <main aria-label="snapshot-lookup">
      <h1>{t('m17.snapshotLookup.title')}</h1>
      {loadError ? <p role="alert">{loadError}</p> : null}
      {vendorError ? <p role="alert">{vendorError}</p> : null}
      <form onSubmit={onSubmit} aria-label="snapshot-lookup-form" noValidate onSubmitCapture={validateDateSubmission}>
        <label htmlFor="sl-vendor">{t('m17.snapshotLookup.vendor')}</label>
        <select id="sl-vendor" value={vendorId} onChange={(e) => setVendorId(e.target.value)}>
          <option value="">{t('m17.snapshotLookup.selectPrompt')}</option>
          {vendors.map((v) => <option key={v.vendorId} value={v.vendorId}>{v.vendorCode} — {v.vendorName}</option>)}
        </select>
        <label htmlFor="sl-item">{t('m17.snapshotLookup.brandingItem')}</label>
        <select id="sl-item" value={brandingItemId} onChange={(e) => setBrandingItemId(e.target.value)}>
          <option value="">{t('m17.snapshotLookup.selectPrompt')}</option>
          {brandingItems.map((b) => <option key={b.brandingItemId} value={b.brandingItemId}>{b.label}</option>)}
        </select>
        <GeographyCascade idPrefix="sl-geo" testIdPrefix="sl-geo" onChange={setGeo} />
        <label htmlFor="sl-from">{t('m17.snapshotLookup.fromDate')}</label>
        <DateInput id="sl-from" type="date" value={fromDate} max={toDate || undefined} onValueChange={(nextValue) => setFromDate(nextValue)} />
        <label htmlFor="sl-to">{t('m17.snapshotLookup.toDate')}</label>
        <DateInput id="sl-to" type="date" value={toDate} min={fromDate || undefined} onValueChange={(nextValue) => setToDate(nextValue)} />
        <button type="submit">{t('m17.snapshotLookup.lookupBtn')}</button>
      </form>
      {error ? <p role="alert">{error}</p> : null}
      {rates && rates.length === 0 ? <p role="status" aria-label="snapshot-none">{t('m17.snapshotLookup.noRate')}</p> : null}
      {rates && rates.length > 0 ? (
        <section aria-label="snapshot-result">
          <h2>{t('m17.snapshotLookup.resolvedRate')}</h2>
          <DataTable<RateInEffect>
            ariaLabel="rates-in-effect"
            rows={rates}
            rowKey={(r) => r.rateId}
            rowAriaLabel={(r) => `rate-${r.rateCode}`}
            columns={[
              { key: 'rateCode', header: t('m17.snapshotLookup.rateCode'), render: (r) => r.rateCode },
              { key: 'rate', header: t('m17.snapshotLookup.rate'), render: (r) => money(Number(r.rateValue)) },
              { key: 'uom', header: t('m17.snapshotLookup.uom'), render: (r) => unitLabel(r.unitOfMeasure) },
              { key: 'validity', header: t('m17.snapshotLookup.validity'), render: (r) => dayRange(r.effectiveFromDate, r.effectiveToDate) },
            ]}
          />
        </section>
      ) : null}
    </main>
  );
}
